import React, { useState, Fragment } from 'react';
import { SearchIcon, FilterIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, PackageIcon, ArrowRightIcon, TagIcon, BoxIcon, UtensilsIcon, Loader2Icon, ScissorsIcon, XIcon, AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { PinVerificationModal } from './PinVerificationModal';
interface MaterialDetails {
  batchNumber: string;
  length: string;
  caliber: string;
  quantity: string;
}
interface Material {
  name: string;
  batchNumber: string;
  quantity: string;
  unit: string;
  details?: MaterialDetails[];
}
interface ProductionStep {
  step: 'kutterei' | 'fuellerei' | 'raeucherei' | 'schaelerei' | 'verpackung';
  status: 'completed' | 'in_progress' | 'pending';
  startTime?: string;
  completionTime?: string;
  batchNumber: string;
  materials: Material[];
  operator?: string;
}
interface TraceabilityRecord {
  id: string;
  mainBatchNumber: string;
  productName: string;
  productionDate: string;
  status: 'completed' | 'in_progress';
  quantity: number;
  productionSteps: ProductionStep[];
  fTraceStatus?: 'pending' | 'transferred';
}
const mockTraceabilityData: TraceabilityRecord[] = [{
  id: '1',
  mainBatchNumber: '130901230425',
  productName: 'Pfefferbeißer Pikante LIDL QS ITW',
  productionDate: '2024-01-20',
  status: 'completed',
  quantity: 1000,
  fTraceStatus: 'pending',
  productionSteps: [{
    step: 'kutterei',
    status: 'completed',
    startTime: '2024-01-20T08:00:00',
    completionTime: '2024-01-20T09:30:00',
    batchNumber: 'KUT-2024-0001',
    operator: 'K. Schmidt',
    materials: [{
      name: 'Schweineschulter',
      batchNumber: '116425',
      quantity: '579,5',
      unit: 'kg'
    }, {
      name: 'Maturation Starter',
      batchNumber: '4414763178',
      quantity: '250',
      unit: 'kg'
    }, {
      name: 'Gewürzmischung Pfefferbeisserpikant',
      batchNumber: '20250415 - 38708',
      quantity: '4,88',
      unit: 'kg'
    }, {
      name: 'NPS',
      batchNumber: '20250325-37063',
      quantity: '4,837',
      unit: 'kg'
    }]
  }, {
    step: 'fuellerei',
    status: 'completed',
    startTime: '2024-01-20T09:45:00',
    completionTime: '2024-01-20T11:00:00',
    batchNumber: 'FUE-2024-0001',
    operator: 'M. Weber',
    materials: [{
      name: 'Naturdarm 28/30',
      batchNumber: 'DA-2024-0089',
      quantity: '10',
      unit: 'Bund',
      details: [{
        batchNumber: 'DA-2024-0089-1',
        length: '91',
        caliber: '28/30',
        quantity: '1'
      }, {
        batchNumber: 'DA-2024-0089-2',
        length: '89',
        caliber: '28/30',
        quantity: '1'
      }, {
        batchNumber: 'DA-2024-0089-3',
        length: '92',
        caliber: '28/30',
        quantity: '1'
      }, {
        batchNumber: 'DA-2024-0089-4',
        length: '90',
        caliber: '28/30',
        quantity: '1'
      }, {
        batchNumber: 'DA-2024-0089-5',
        length: '88',
        caliber: '28/30',
        quantity: '1'
      }]
    }]
  }, {
    step: 'raeucherei',
    status: 'completed',
    startTime: '2024-01-20T11:30:00',
    completionTime: '2024-01-20T14:30:00',
    batchNumber: 'RAE-2024-0001',
    operator: 'R. Mueller',
    materials: [{
      name: 'Räucherliquid',
      batchNumber: 'RL-2024-0012',
      quantity: '5',
      unit: 'L'
    }]
  }, {
    step: 'schaelerei',
    status: 'completed',
    startTime: '2024-01-20T14:45:00',
    completionTime: '2024-01-20T16:00:00',
    batchNumber: 'SCH-2024-0001',
    operator: 'S. Fischer',
    materials: [{
      name: 'Palette',
      batchNumber: 'PAL-2024-0001',
      quantity: '1',
      unit: 'Stück'
    }]
  }, {
    step: 'verpackung',
    status: 'completed',
    startTime: '2024-01-20T16:15:00',
    completionTime: '2024-01-20T17:30:00',
    batchNumber: 'VER-2024-0001',
    operator: 'V. Müller',
    materials: [{
      name: 'Kundencharge',
      batchNumber: 'KUNDE-2024-001',
      quantity: '1000',
      unit: 'Stück'
    }]
  }]
}];
const productionStepIcons = {
  kutterei: BoxIcon,
  fuellerei: UtensilsIcon,
  raeucherei: Loader2Icon,
  schaelerei: ScissorsIcon,
  verpackung: PackageIcon
};
const productionStepLabels = {
  kutterei: 'Kutterei',
  fuellerei: 'Füllerei',
  raeucherei: 'Räucherei',
  schaelerei: 'Schälerei',
  verpackung: 'Verpackung'
};
interface RetailerSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecords: string[];
  onTransfer: (retailer: string) => void;
}
function RetailerSelectionModal({
  isOpen,
  onClose,
  selectedRecords,
  onTransfer
}: RetailerSelectionModalProps) {
  const [selectedRetailer, setSelectedRetailer] = useState<string>('');
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-911">
      <div className="bg-white rounded-lg p-6 w-[500px]" data-id="element-912">
        <div className="flex justify-between items-center mb-4" data-id="element-913">
          <h2 className="text-lg font-medium" data-id="element-914">F-Trace Übertragung</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-915">
            <XIcon className="h-5 w-5" data-id="element-916" />
          </button>
        </div>
        <div className="mb-4" data-id="element-917">
          <p className="text-sm text-gray-600 mb-2" data-id="element-918">
            {selectedRecords.length} Datensätze ausgewählt
          </p>
          <div className="space-y-2" data-id="element-919">
            <label className="block text-sm font-medium text-gray-700" data-id="element-920">
              Händler auswählen
            </label>
            <select className="w-full border border-gray-300 rounded-lg p-2" value={selectedRetailer} onChange={e => setSelectedRetailer(e.target.value)} data-id="element-921">
              <option value="" data-id="element-922">Bitte wählen...</option>
              <option value="netto" data-id="element-923">Netto</option>
              <option value="aldi" data-id="element-924">Aldi</option>
              <option value="edeka" data-id="element-925">Edeka</option>
              <option value="rewe" data-id="element-926">Rewe</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3" data-id="element-927">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-928">
            Abbrechen
          </button>
          <button onClick={() => onTransfer(selectedRetailer)} disabled={!selectedRetailer} className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-929">
            Daten übertragen
          </button>
        </div>
      </div>
    </div>;
}
interface TransferProgressModalProps {
  isOpen: boolean;
  progress: number;
}
function TransferProgressModal({
  isOpen,
  progress
}: TransferProgressModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-930">
      <div className="bg-white rounded-lg p-6 w-[400px]" data-id="element-931">
        <div className="text-center mb-4" data-id="element-932">
          <h2 className="text-lg font-medium mb-2" data-id="element-933">Datenübertragung</h2>
          <p className="text-sm text-gray-600" data-id="element-934">Übertrage Daten an F-Trace...</p>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-935">
          <div className="bg-[#ec8301] h-2 rounded-full transition-all duration-300" style={{
          width: `${progress}%`
        }} data-id="element-936"></div>
        </div>
        {progress === 100 && <div className="mt-4 text-center text-green-600 flex items-center justify-center gap-2" data-id="element-937">
            <CheckIcon className="h-5 w-5" data-id="element-938" />
            <span data-id="element-939">Übertragung abgeschlossen</span>
          </div>}
      </div>
    </div>;
}
export function Traceability() {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed'>('all');
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [showRetailerModal, setShowRetailerModal] = useState(false);
  const [showTransferProgress, setShowTransferProgress] = useState(false);
  const [transferProgress, setTransferProgress] = useState(0);
  const [fTraceMode, setFTraceMode] = useState(false);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(true);
  const toggleRow = (id: string) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  const handleTransfer = (retailer: string) => {
    setShowRetailerModal(false);
    setShowTransferProgress(true);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setTransferProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setShowTransferProgress(false);
          setTransferProgress(0);
          setSelectedRecords([]);
          setFTraceMode(false);
        }, 1000);
      }
    }, 500);
  };
  const filteredData = mockTraceabilityData.filter(record => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesMainBatch = record.mainBatchNumber.toLowerCase().includes(searchTermLower);
    const matchesProductName = record.productName.toLowerCase().includes(searchTermLower);
    const matchesInSteps = record.productionSteps.some(step => {
      const matchesStepBatch = step.batchNumber.toLowerCase().includes(searchTermLower);
      const matchesInMaterials = step.materials.some(material => {
        const matchesMaterialBatch = material.batchNumber.toLowerCase().includes(searchTermLower);
        const matchesInDetails = material.details?.some(detail => detail.batchNumber.toLowerCase().includes(searchTermLower)) || false;
        return matchesMaterialBatch || matchesInDetails;
      });
      return matchesStepBatch || matchesInMaterials;
    });
    const matchesSearch = matchesMainBatch || matchesProductName || matchesInSteps;
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    const matchesDate = (!dateRange.start || record.productionDate >= dateRange.start) && (!dateRange.end || record.productionDate <= dateRange.end);
    return matchesSearch && matchesStatus && matchesDate;
  });
  const highlightText = (text: string) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => part.toLowerCase() === searchTerm.toLowerCase() ? <span key={index} className="bg-yellow-200" data-id="element-940">
          {part}
        </span> : part);
  };
  if (!isPinVerified) {
    return <div className="p-6 h-full" data-id="element-941">
        <header className="mb-8" data-id="element-942">
          <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-943">Rückverfolgung</h1>
          <p className="text-gray-600" data-id="element-944">
            Verfolgen Sie die komplette Produktionshistorie
          </p>
        </header>
        <div className="flex items-center justify-center h-[calc(100%-8rem)]" data-id="element-945">
          <div className="text-center" data-id="element-946">
            <p className="text-xl text-gray-600 mb-2" data-id="element-947">
              Dieser Bereich ist PIN-geschützt
            </p>
            <p className="text-gray-500 mb-4" data-id="element-948">
              Bitte geben Sie Ihre PIN ein, um fortzufahren
            </p>
            <button onClick={() => setShowPinModal(true)} className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90" data-id="element-949">
              PIN eingeben
            </button>
          </div>
        </div>
        <PinVerificationModal isOpen={showPinModal} onClose={() => setShowPinModal(false)} onVerify={() => {
        setIsPinVerified(true);
        setShowPinModal(false);
      }} data-id="element-950" />
      </div>;
  }
  return <div className="p-6 h-full" data-id="element-951">
      <header className="mb-8" data-id="element-952">
        <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-953">Rückverfolgung</h1>
        <p className="text-gray-600" data-id="element-954">
          Verfolgen Sie die komplette Produktionshistorie
        </p>
      </header>
      <div className="bg-white rounded-lg shadow" data-id="element-955">
        <div className="p-4 border-b border-gray-200" data-id="element-956">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-957">
            <div className="relative" data-id="element-958">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-959" />
              <input type="text" placeholder="Charge oder Produkt suchen..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-960" />
            </div>
            <div className="relative" data-id="element-961">
              <input type="date" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={dateRange.start} onChange={e => setDateRange(prev => ({
              ...prev,
              start: e.target.value
            }))} data-id="element-962" />
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-963" />
            </div>
            <div className="relative" data-id="element-964">
              <input type="date" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={dateRange.end} onChange={e => setDateRange(prev => ({
              ...prev,
              end: e.target.value
            }))} data-id="element-965" />
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-966" />
            </div>
            <div className="relative" data-id="element-967">
              <select className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301] appearance-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | 'completed')} data-id="element-968">
                <option value="all" data-id="element-969">Alle Status</option>
                <option value="completed" data-id="element-970">Abgeschlossen</option>
              </select>
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-971" />
            </div>
            <button onClick={() => setFTraceMode(!fTraceMode)} className={`px-4 py-2 rounded-lg border flex items-center justify-center gap-2 ${fTraceMode ? 'bg-[#ec8301] text-white' : 'border-[#ec8301] text-[#ec8301]'}`} data-id="element-972">
              <span data-id="element-973">F-Trace</span>
              {selectedRecords.length > 0 && <span className="bg-white text-[#ec8301] px-2 py-0.5 rounded-full text-sm" data-id="element-974">
                  {selectedRecords.length}
                </span>}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-975">
          <table className="w-full" data-id="element-976">
            <thead className="bg-gray-50" data-id="element-977">
              <tr data-id="element-978">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-979">
                  Chargen-Nr.
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-980">
                  Produkt
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-981">
                  Datum
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-982">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" data-id="element-983">
                  Menge
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-984">
              {filteredData.map(record => <Fragment key={record.id} data-id="element-985">
                  <tr onClick={() => fTraceMode ? setSelectedRecords(prev => prev.includes(record.id) ? prev.filter(id => id !== record.id) : [...prev, record.id]) : toggleRow(record.id)} className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRows.includes(record.id) ? 'bg-gray-50' : ''}`} data-id="element-986">
                    <td className="px-4 py-3 font-mono text-sm" data-id="element-987">
                      <div className="flex items-center gap-2" data-id="element-988">
                        {record.fTraceStatus === 'pending' && <AlertTriangleIcon className="h-5 w-5 text-[#ec8301]" data-id="element-989" />}
                        {fTraceMode && <input type="checkbox" checked={selectedRecords.includes(record.id)} onChange={() => {}} className="h-4 w-4 text-[#ec8301] rounded border-gray-300" data-id="element-990" />}
                        {highlightText(record.mainBatchNumber)}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-991">
                      {highlightText(record.productName)}
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-992">
                      {new Date(record.productionDate).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-3" data-id="element-993">
                      <span className={`px-2 py-1 rounded-full text-sm ${record.status === 'completed' ? 'bg-green-100 text-green-800' : record.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-994">
                        {record.status === 'completed' ? 'Abgeschlossen' : record.status === 'in_progress' ? 'In Bearbeitung' : 'Geplant'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-995">
                      {record.quantity} Stück
                    </td>
                  </tr>
                  {expandedRows.includes(record.id) && <tr data-id="element-996">
                      <td colSpan={6} className="px-4 py-3 bg-gray-50" data-id="element-997">
                        <div className="space-y-8" data-id="element-998">
                          {record.productionSteps.map((step, stepIndex) => <div key={stepIndex} className="space-y-4" data-id="element-999">
                              <div className="flex items-center gap-2" data-id="element-1000">
                                {(() => {
                          const Icon = productionStepIcons[step.step];
                          return <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.status === 'completed' ? 'bg-green-500' : step.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-1001">
                                      <Icon className="h-4 w-4 text-white" data-id="element-1002" />
                                    </div>;
                        })()}
                                <div data-id="element-1003">
                                  <h3 className="text-lg font-medium" data-id="element-1004">
                                    {productionStepLabels[step.step]}
                                  </h3>
                                  <p className="text-sm text-gray-500" data-id="element-1005">
                                    Chargen-Nr.{' '}
                                    {highlightText(step.batchNumber)}
                                  </p>
                                </div>
                                <div className={`ml-4 px-3 py-1 rounded-full text-sm ${step.status === 'completed' ? 'bg-green-100 text-green-800' : step.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1006">
                                  {step.status === 'completed' ? 'Abgeschlossen' : step.status === 'in_progress' ? 'In Bearbeitung' : 'Ausstehend'}
                                </div>
                              </div>
                              {step.materials && step.materials.length > 0 && <div className="ml-10" data-id="element-1007">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1008">
                                    {step.step === 'schaelerei' ? 'Paletten-Information:' : step.step === 'verpackung' ? 'Kunden-Information:' : 'Verwendete Rohstoffe:'}
                                  </h4>
                                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1009">
                                    <table className="w-full text-sm" data-id="element-1010">
                                      <thead className="bg-gray-50" data-id="element-1011">
                                        <tr data-id="element-1012">
                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1013">
                                            {step.step === 'schaelerei' ? 'Paletten-Nr.' : step.step === 'verpackung' ? 'Kunden-Charge' : 'Material'}
                                          </th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1014">
                                            Chargen-Nr.
                                          </th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1015">
                                            Menge
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200" data-id="element-1016">
                                        {step.materials.map((material, idx) => <Fragment key={idx} data-id="element-1017">
                                            <tr className="hover:bg-gray-50" data-id="element-1018">
                                              <td className="px-4 py-2" data-id="element-1019">
                                                {material.name}
                                              </td>
                                              <td className="px-4 py-2 font-mono" data-id="element-1020">
                                                {highlightText(material.batchNumber)}
                                              </td>
                                              <td className="px-4 py-2" data-id="element-1021">
                                                {material.quantity}{' '}
                                                {material.unit}
                                              </td>
                                            </tr>
                                            {material.details && <tr className="bg-gray-50" data-id="element-1022">
                                                <td colSpan={3} className="px-4 py-2" data-id="element-1023">
                                                  <div className="ml-6" data-id="element-1024">
                                                    <table className="w-full text-sm" data-id="element-1025">
                                                      <thead className="bg-white" data-id="element-1026">
                                                        <tr data-id="element-1027">
                                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1028">
                                                            Einzelchargen-Nr.
                                                          </th>
                                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1029">
                                                            Länge (m)
                                                          </th>
                                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1030">
                                                            Kaliber
                                                          </th>
                                                          <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1031">
                                                            Menge (Bund)
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody className="divide-y divide-gray-100" data-id="element-1032">
                                                        {material.details.map((detail, detailIdx) => <tr key={detailIdx} className="hover:bg-gray-100" data-id="element-1033">
                                                              <td className="px-4 py-2 font-mono text-sm" data-id="element-1034">
                                                                {highlightText(detail.batchNumber)}
                                                              </td>
                                                              <td className="px-4 py-2" data-id="element-1035">
                                                                {detail.length}
                                                              </td>
                                                              <td className="px-4 py-2" data-id="element-1036">
                                                                {detail.caliber}
                                                              </td>
                                                              <td className="px-4 py-2" data-id="element-1037">
                                                                {detail.quantity}
                                                              </td>
                                                            </tr>)}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>}
                                          </Fragment>)}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>}
                              {step.operator && <div className="ml-10 text-sm text-gray-500" data-id="element-1038">
                                  <div data-id="element-1039">
                                    Bearbeiter: {highlightText(step.operator)}
                                  </div>
                                  <div data-id="element-1040">
                                    Datum:{' '}
                                    {new Date(step.startTime!).toLocaleDateString('de-DE')}
                                  </div>
                                </div>}
                            </div>)}
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
      {fTraceMode && selectedRecords.length > 0 && <div className="fixed bottom-6 right-6" data-id="element-1041">
          <button onClick={() => setShowRetailerModal(true)} className="bg-[#ec8301] text-white px-6 py-3 rounded-lg shadow-lg hover:bg-[#ec8301]/90 flex items-center gap-2" data-id="element-1042">
            <span data-id="element-1043">Übertragen</span>
            <ArrowRightIcon className="h-5 w-5" data-id="element-1044" />
          </button>
        </div>}
      <RetailerSelectionModal isOpen={showRetailerModal} onClose={() => setShowRetailerModal(false)} selectedRecords={selectedRecords} onTransfer={handleTransfer} data-id="element-1045" />
      <TransferProgressModal isOpen={showTransferProgress} progress={transferProgress} data-id="element-1046" />
    </div>;
}